/* designTokens — util bersama storefront + Live Preview (parity terjamin).
   Cermin dari backend services/storefront_config.py normalize_hex. */

export const normalizeHex = (value) => {
  const v = String(value || "").trim();
  if (!v) return "";
  const m = v.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
  if (!m) return "";
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 8) h = h.slice(0, 6);
  return "#" + h.toUpperCase();
};

export const isDarkColor = (hex) => {
  const h = normalizeHex(hex);
  if (!h) return false;
  const r = parseInt(h.slice(1, 3), 16), g = parseInt(h.slice(3, 5), 16), b = parseInt(h.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
};

export const radiusFor = (buttonStyle) =>
  buttonStyle === "pill" ? "9999px" : buttonStyle === "sharp" ? "2px" : "12px";

export const cardRadiusFor = (cardStyle) =>
  cardStyle === "sharp" ? "2px" : cardStyle === "premium" ? "20px" : "16px";

export const gridColsFor = (productLayout) => {
  if (productLayout === "grid-2-col") return "grid-cols-2";
  if (productLayout === "grid-3-col") return "grid-cols-2 sm:grid-cols-3";
  return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"; // grid-4-col / list / default
};

/* ===== Branding System V2 — role-based tokens ===== */

/* Typography presets storefront (admin memilih preset, bukan font bebas). */
export const TYPOGRAPHY_PRESETS = {
  "modern-commerce": { label: "Modern Commerce", heading: "Inter", body: "Inter" },
  friendly: { label: "Friendly", heading: "Poppins", body: "Poppins" },
  premium: { label: "Premium", heading: "Playfair Display", body: "Inter" },
  tech: { label: "Tech", heading: "Space Grotesk", body: "Space Grotesk" },
};

/* Tipografi global (Phase 3): preset "custom" → admin memilih Heading/Body dari whitelist ini (sinkron backend
   FONT_WHITELIST). Tanpa preset custom → jalur lama (preset/template) tidak berubah. */
export const TYPOGRAPHY_CUSTOM = "custom";
export const FONT_WHITELIST = [
  "Inter", "Poppins", "Plus Jakarta Sans", "Manrope", "DM Sans", "Nunito", "Lato", "Montserrat", "Raleway",
  "Work Sans", "Sora", "Outfit", "Urbanist", "Space Grotesk", "Bricolage Grotesque", "Playfair Display", "Lora",
  "Merriweather",
];
export const isWhitelistedFont = (f) => FONT_WHITELIST.includes(f);
/* Resolusi font storefront: custom (whitelist) > preset > font template/tenant lama. `extended` = heading
   diterapkan ke semua judul storefront (bukan hanya .font-display) — hanya untuk custom. */
export function resolveTypography(theme = {}) {
  if (theme.typography_preset === TYPOGRAPHY_CUSTOM) {
    return {
      heading: isWhitelistedFont(theme.font_heading) ? theme.font_heading : "",
      body: isWhitelistedFont(theme.font_body) ? theme.font_body : "",
      extended: true,
    };
  }
  const preset = TYPOGRAPHY_PRESETS[theme.typography_preset];
  return { heading: preset ? preset.heading : theme.font_heading, body: preset ? preset.body : theme.font_body, extended: false };
}

/* Kontras foreground otomatis untuk warna solid. */
export const readableOn = (hex) => (isDarkColor(hex) ? "#FFFFFF" : "#111827");

/* Default Commerce/Action colors — dark-aware supaya tenant dark mode
   (mis. vape #0F172A) tetap terbaca tanpa konfigurasi tambahan. */
export const commerceDefaults = (background) => {
  if (isDarkColor(background)) {
    return {
      price: "#F9FAFB",
      saleBadge: "#EF4444",
      btnPrimary: "#F9FAFB",
      btnPrimaryFg: "#111827",
      btnSecondary: "color-mix(in srgb, #FFFFFF 14%, transparent)",
      btnSecondaryFg: "#F9FAFB",
    };
  }
  return {
    price: "#111827",
    saleBadge: "#EF4444",
    btnPrimary: "#111827",
    btnPrimaryFg: "#FFFFFF",
    btnSecondary: "#F3F4F6",
    btnSecondaryFg: "#111827",
  };
};

/* Semantic content text tokens — Storefront Semantic Text Isolation.
   Diturunkan dari BACKGROUND (bukan dari Branding Text Color pilihan tenant)
   supaya konten komersial (judul produk, form, akun) selalu terbaca. */
export const contentTokens = (background) =>
  isDarkColor(background)
    ? { text: "#F1F5F9", muted: "#94A3B8", heading: "#F8FAFC" }
    : { text: "#1A1A1A", muted: "#5C5C5C", heading: "#0E0E0E" };

/* Turunan surface tokens dark-aware — dipakai BrandingContext & Live Preview. */
export const deriveSurfaces = (background, text) => {
  const bg = normalizeHex(background) || "#FAF9F6";
  const tx = normalizeHex(text) || "#0E0E0E";
  if (isDarkColor(bg)) {
    return {
      surface: `color-mix(in srgb, ${bg} 88%, white)`,
      border: `color-mix(in srgb, ${tx} 14%, transparent)`,
      muted: `color-mix(in srgb, ${tx} 62%, ${bg})`,
      dark: true,
    };
  }
  return { surface: "#FFFFFF", border: "#E5E5E5", muted: "#5C5C5C", dark: false };
};

/* Semantic Action Color Isolation — Navigation layer (bg-aware).
   Header/bottom-nav mengikuti kegelapan background tenant, BUKAN brand color. */
export const navTokens = (background) =>
  isDarkColor(background)
    ? { action: "#F9FAFB", actionFg: "#111827", actionHover: "#E5E7EB", avatarBg: "#F3F4F6", avatarFg: "#111827" }
    : { action: "#111827", actionFg: "#FFFFFF", actionHover: "#1F2937", avatarBg: "#1F2937", avatarFg: "#FFFFFF" };
